import { styled, useTheme } from '@mui/material/styles';
import { IconButton, Link } from '@mui/material';
import { Typography, Box } from '@mui/material';
import Dot from 'components/common/dot/Dot';
import { UserType } from 'hooks/useUserData';
import MailLineIcon from 'remixicon-react/MailLineIcon';
import MapPinLineIcon from 'remixicon-react/MapPinLineIcon';
import PhoneLineIcon from 'remixicon-react/PhoneLineIcon';
import useCompanyQueryData from 'hooks/useCompanyQueryData';
import { ReactComponent as SMEBadge } from 'assets/icons/sme-badge.svg';
import Edit2LineIcon from 'remixicon-react/Edit2LineIcon';
import { Dispatch, SetStateAction, useMemo } from 'react';

type ProfileMetaInfoProps = {
  user: UserType;
  isLoggedInUser: boolean;
  setOpenEditProfile: Dispatch<SetStateAction<boolean>>;
};

function ProfileMetaInfo({ user, isLoggedInUser, setOpenEditProfile }: ProfileMetaInfoProps) {
  const { shouldDisplayCompanyOnUserCards } = useCompanyQueryData();
  const theme = useTheme();

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {user.userNameToDisplay}
        </Typography>
        {isLoggedInUser && (
          <Box sx={{ ml: 2 }}>
            <IconButton onClick={() => setOpenEditProfile(true)} title="Edit">
              <Edit2LineIcon color={theme.palette.custom.lightBlack} />
            </IconButton>
          </Box>
        )}
      </Box>

      {(user.department_or_region ||
        user.title ||
        user?.primaryCompanyName ||
        user?.userPrimaryCompanyName) && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', marginTop: '4px' }}>
          <Box display="flex" alignItems="center" sx={{ color: theme.palette.custom.activeTitle }}>
            {user.department_or_region && (
              <>
                <Typography title="Department/Region">{user.department_or_region}</Typography>
                <Dot />
              </>
            )}
            <Typography title="Job Title">{user.title}</Typography>
            {shouldDisplayCompanyOnUserCards &&
              (user?.primaryCompanyName || user?.userPrimaryCompanyName) && (
                <>
                  <Dot />
                  <Typography title="Company">
                    {user?.primaryCompanyName || user?.userPrimaryCompanyName}
                  </Typography>
                </>
              )}
          </Box>
          {user?.SMESkills && user?.SMESkills.length > 0 && (
            <Box
              sx={(theme) => ({
                color: theme.palette.custom.link,
                backgroundColor: theme.palette.custom.aliceBlue,
                display: 'flex',
                alignItems: 'center',
                borderRadius: '4px',
                padding: '2px 8px 2px 8px',
                marginLeft: 1,
              })}
            >
              <SMEBadge style={{ width: 16, height: 16, marginRight: 2 }} />
              <Typography>SME</Typography>
            </Box>
          )}
        </Box>
      )}
      {(user.city || user.state_code || user.country_code) && (
        <Typography sx={{ color: theme.palette.custom.label, marginTop: 0.5 }}>
          <MapPinLineIcon size={16} />{' '}
          {`${user.city || ''}|${user.state_code || ''}|${user.country_code || ''}`
            .split('|')
            .join(', ')}
        </Typography>
      )}
      {((user?.department && user?.department?.length > 0) ||
        (user?.region && user?.region?.length > 0)) && (
        <Box>
          {user?.department && user?.department?.length > 0 && (
            <Text display="inline" title="Departments">
              {user.department.join(', ')}
            </Text>
          )}
          {user?.department &&
            user?.department?.length > 0 &&
            user?.region &&
            user?.region?.length > 0 && <Dot sx={{ display: 'inline' }} />}
          {user?.region && user?.region?.length > 0 && (
            <Text display="inline" title="Regions">
              {user.region.join(', ')}
            </Text>
          )}
        </Box>
      )}
      {(user.email || user.phoneNumber) && (
        <Box display="flex" alignItems="center" mt={0.5}>
          {user.email && (
            <Box display="flex" alignItems="center">
              <MailLineIcon size={16} /> &nbsp;&nbsp;
              <MuiLink href={`mailto:${user.email}`}>{user.email}</MuiLink>
            </Box>
          )}
          {user.phoneNumber && <Dot />}
          {user.phoneNumber && (
            <Box display="flex" alignItems="center">
              <PhoneLineIcon size={16} /> &nbsp;&nbsp;
              <MuiLink href={`tel:${user.phoneNumber}`} display="flex" alignItems="center">
                {user.phoneNumber}
              </MuiLink>
            </Box>
          )}
        </Box>
      )}
    </>
  );
}

const MuiLink = styled(Link)(({ theme }) => ({
  color: theme.palette.custom.label,
  textDecoration: 'none',
  '&:hover': {
    color: theme.palette.custom.link,
    textDecoration: 'underline',
  },
}));

const Text = styled(Typography)(({ theme }) => ({
  color: theme.palette.custom.label,
}));

export default ProfileMetaInfo;
